import {
  MutationTree, ActionTree, Module, GetterTree,
} from 'vuex';
import Axios from 'axios';
import LigandAtomDTO from './LigandAtomDTO';
import ReceptorAtomDTO from './ReceptorAtomDTO';
import ModelConfigurationDTO from './ModelConfigurationDTO';
import RedMatOptionsDTO from './RedMatOptionsDTO';
import RedMatCalculationTypes from './RedMatCalculationTypes';
import SetAtomsFromPdbCommand from './SetAtomsFromPdbCommand';
import SetAtomsFromPdbResponse from './SetAtomsFromPdbResponse';
import SetReceptorIrradiatedAtomsCommand from './SetReceptorIrradiatedAtomsCommand';
import SetReceptorIrradiatedAtomsResponse from './SetReceptorIrradiatedAtomsResponse';
import CalculateCommand from './CalculateCommand';
import CalculateResponse from './CalculateResponse';

const endpoint = 'reducedrelaxationmatrix';

const NEW_STATUS = 'NEW_STATUS';
const ERROR_STATUS = 'ERROR_STATUS';
const LOADING_STATUS = 'LOADING_STATUS';
const SUCCESS_STATUS = 'SUCCESS_STATUS';
const SET_LIGAND_ATOMS = 'SET_LIGAND_ATOMS';
const SET_RECEPTOR_ATOMS = 'SET_RECEPTOR_ATOMS';
const SET_MODEL_CONFIGURATIONS = 'SET_MODEL_CONFIGURATIONS';

export const CLEAR_STATE = 'CLEAR_STATE';
export const INCREMENT_EDIT_STEP = 'INCREMENT_EDIT_STEP';
export const DECREMENT_EDIT_STEP = 'DECREMENT_EDIT_STEP';
export const SET_CALCULATION_TYPE = 'SET_CALCULATION_TYPE';
export const SET_MD_FILES = 'SET_MD_FILES';
export const ATOMS_FROM_PDB_REQUEST = 'ATOMS_FROM_PDB_REQUEST';
export const SET_EXPERIMENTAL_STDS = 'SET_EXPERIMENTAL_STDS';
export const IRRADIATED_RECEPTOR_ATOMS_REQUEST = 'IRRADIATED_RECEPTOR_ATOMS_REQUEST';
export const SET_OPTIONS = 'SET_OPTIONS';
export const CALCULATE_REQUEST = 'CALCULATE_REQUEST';
export const SET_RESULT = 'SET_RESULT';

// getters
export const IS_NEW = 'IS_NEW';
export const IS_LOADING = 'IS_LOADING';
export const IS_ERROR = 'IS_ERROR';
export const MESSAGE = 'MESSAGE';
export const HAS_RESULT = 'HAS_RESULT';

export interface RedMatCalculationState {
  status: string;
  statusMessage: string;
  currentEditStep: number;
  amberTopology: File | null;
  amberTrajectory: File | null;
  calculationType: RedMatCalculationTypes;
  ligandAtoms: LigandAtomDTO[];
  receptorAtoms: ReceptorAtomDTO[];
  modelConfigurations: ModelConfigurationDTO[];
  options: RedMatOptionsDTO;
  result: CalculateResponse | null;
}

export interface MdFiles {
  topology: File | null;
  trajectory: File | null;
}

const redMatCalculationState: RedMatCalculationState = {
  status: NEW_STATUS,
  statusMessage: '',
  currentEditStep: 1,
  amberTopology: null,
  amberTrajectory: null,
  calculationType: RedMatCalculationTypes.Docking,
  ligandAtoms: [],
  receptorAtoms: [],
  modelConfigurations: [],
  options: new RedMatOptionsDTO(),
  result: null,
};

const mutations: MutationTree<RedMatCalculationState> = {
  [ERROR_STATUS]: (state, message: string) => {
    state.status = ERROR_STATUS;
    state.statusMessage = message || '';
  },
  [LOADING_STATUS]: (state, message: string) => {
    state.status = LOADING_STATUS;
    state.statusMessage = message || '';
  },
  [SUCCESS_STATUS]: (state, message: string) => {
    state.status = SUCCESS_STATUS;
    state.statusMessage = message || '';
  },
  [CLEAR_STATE]: (state) => {
    state.status = IS_NEW;
    state.statusMessage = '';
    state.currentEditStep = 1;
    state.amberTopology = null;
    state.amberTrajectory = null;
    state.calculationType = RedMatCalculationTypes.Docking;
    state.ligandAtoms = [];
    state.receptorAtoms = [];
    state.modelConfigurations = [];
    state.options = new RedMatOptionsDTO();
  },
  [INCREMENT_EDIT_STEP]: (state) => {
    state.currentEditStep += 1;
  },
  [DECREMENT_EDIT_STEP]: (state) => {
    state.currentEditStep -= 1;
  },
  [SET_CALCULATION_TYPE]: (state, type: RedMatCalculationTypes) => {
    state.calculationType = type;
  },
  [SET_MD_FILES]: (state, mdFiles: MdFiles) => {
    state.amberTopology = mdFiles.topology;
    state.amberTrajectory = mdFiles.trajectory;
  },
  [SET_LIGAND_ATOMS]: (state, ligandAtoms: LigandAtomDTO[]) => {
    state.ligandAtoms = ligandAtoms;
  },
  [SET_RECEPTOR_ATOMS]: (state, receptorAtoms: ReceptorAtomDTO[]) => {
    state.receptorAtoms = receptorAtoms;
  },
  [SET_MODEL_CONFIGURATIONS]: (state, modelConfigurations: ModelConfigurationDTO[]) => {
    state.modelConfigurations = modelConfigurations;
  },
  [SET_OPTIONS]: (state, options: RedMatOptionsDTO) => {
    state.options = options;
  },
  [SET_RESULT]: (state, result: CalculateResponse) => {
    state.result = result;
  },
};

const actions: ActionTree<RedMatCalculationState, object> = {
  [INCREMENT_EDIT_STEP]: ({ commit }) => new Promise<void>((resolve) => {
    commit(INCREMENT_EDIT_STEP);

    return resolve();
  }),
  [DECREMENT_EDIT_STEP]: ({ commit }) => new Promise<void>((resolve) => {
    commit(DECREMENT_EDIT_STEP);

    return resolve();
  }),
  [SET_CALCULATION_TYPE]: (
    { commit },
    type: RedMatCalculationTypes,
  ) => new Promise<void>((resolve) => {
    commit(SUCCESS_STATUS); // required to pass the route guard
    commit(SET_CALCULATION_TYPE, type);

    return resolve();
  }),
  [SET_MD_FILES]: (
    { commit },
    mdFiles: MdFiles,
  ) => new Promise<void>((resolve, reject) => {
    if (
      (mdFiles.topology === null && mdFiles.trajectory === null)
      || (mdFiles.topology !== null && mdFiles.trajectory !== null)
    ) {
      commit(SET_MD_FILES, mdFiles);

      return resolve();
    }
    commit(ERROR_STATUS, 'Error setting MD trajectory files');

    return reject();
  }),
  [ATOMS_FROM_PDB_REQUEST]: (
    { commit },
    command: SetAtomsFromPdbCommand,
  ) => new Promise<void>((resolve, reject) => {
    commit(LOADING_STATUS);

    const formData = new FormData();
    formData.append('ligandPdb', command.ligandPdb);
    formData.append('receptorPdb', command.receptorPdb);

    return Axios.post<SetAtomsFromPdbResponse>(
      `${endpoint}/atoms`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )
      .then(({ data }) => {
        commit(SUCCESS_STATUS);

        commit(SET_LIGAND_ATOMS, data.ligandAtoms);
        commit(SET_RECEPTOR_ATOMS, data.receptorAtoms);
        commit(SET_MODEL_CONFIGURATIONS, data.modelConfigurations);

        return resolve();
      })
      .catch(() => {
        commit(ERROR_STATUS, 'Error processing ligand or receptor PDB. Common issues: ligand and receptor atom numbers overlap');

        return reject();
      });
  }),
  [SET_EXPERIMENTAL_STDS]: (
    { commit },
    ligandAtoms: LigandAtomDTO[],
  ) => new Promise<void>((resolve) => {
    commit(SET_LIGAND_ATOMS, ligandAtoms);

    return resolve();
  }),
  [IRRADIATED_RECEPTOR_ATOMS_REQUEST]: (
    { commit },
    command: SetReceptorIrradiatedAtomsCommand,
  ) => new Promise<void>((resolve, reject) => {
    commit(LOADING_STATUS);

    const formData = new FormData();
    command.receptorAtoms.forEach((ra, i) => {
      formData.append(`receptorAtoms[${i}][atomNumber]`, ra.atomNumber.toString());
      formData.append(`receptorAtoms[${i}][atomName]`, ra.atomName);
      formData.append(`receptorAtoms[${i}][residueNumber]`, ra.residueNumber.toString());
      formData.append(`receptorAtoms[${i}][residueName]`, ra.residueName);
      formData.append(`receptorAtoms[${i}][isIrradiated]`, ra.isIrradiated ? 'true' : 'false');
    });
    formData.append('filter', command.filter);
    formData.append('shiftX', command.shiftX as File);

    return Axios.post<SetReceptorIrradiatedAtomsResponse>(
      `${endpoint}/irradiated`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )
      .then(({ data }) => {
        commit(SUCCESS_STATUS);
        commit(SET_RECEPTOR_ATOMS, data.receptorAtoms);

        return resolve();
      })
      .catch(() => {
        commit(ERROR_STATUS, 'Error calculating irradiated receptor atoms');

        return reject();
      });
  }),
  [SET_OPTIONS]: (
    { commit },
    options: RedMatOptionsDTO,
  ) => new Promise<void>((resolve) => {
    commit(SET_OPTIONS, options);

    return resolve();
  }),
  [CALCULATE_REQUEST]: ({ commit, state }) => new Promise<void>((resolve, reject) => {
    commit(LOADING_STATUS);

    const command: CalculateCommand = {
      calculationType: state.calculationType,
      ligandAtoms: state.ligandAtoms,
      receptorAtoms: state.receptorAtoms,
      modelConfigurations: state.modelConfigurations,
      options: state.options,
      amberTopology: state.amberTopology,
      amberTrajectory: state.amberTrajectory,
    };

    const formData = new FormData();
    formData.append('calculationType', command.calculationType);
    command.ligandAtoms.forEach((la, i) => {
      formData.append(`ligandAtoms[${i}][atomNumber]`, la.atomNumber.toString());
      formData.append(`ligandAtoms[${i}][atomName]`, la.atomName);
      formData.append(`ligandAtoms[${i}][residueNumber]`, la.residueNumber.toString());
      formData.append(`ligandAtoms[${i}][residueName]`, la.residueName);
      formData.append(`ligandAtoms[${i}][std]`, la.std.toString());
    });
    command.receptorAtoms.forEach((ra, i) => {
      formData.append(`receptorAtoms[${i}][atomNumber]`, ra.atomNumber.toString());
      formData.append(`receptorAtoms[${i}][atomName]`, ra.atomName);
      formData.append(`receptorAtoms[${i}][residueNumber]`, ra.residueNumber.toString());
      formData.append(`receptorAtoms[${i}][residueName]`, ra.residueName);
      formData.append(`receptorAtoms[${i}][isIrradiated]`, ra.isIrradiated ? 'true' : 'false');
    });
    command.modelConfigurations.forEach((mc, i) => {
      formData.append(`modelConfigurations[${i}][title]`, mc.title);
      mc.atomCoordinates.forEach((ac, j) => {
        formData.append(`modelConfigurations[${i}][atomCoordinates][${j}][atomNumber]`, ac.atomNumber.toString());
        formData.append(`modelConfigurations[${i}][atomCoordinates][${j}][atomName]`, ac.atomName);
        formData.append(`modelConfigurations[${i}][atomCoordinates][${j}][residueNumber]`, ac.residueNumber.toString());
        formData.append(`modelConfigurations[${i}][atomCoordinates][${j}][residueName]`, ac.residueName);
        formData.append(`modelConfigurations[${i}][atomCoordinates][${j}][x]`, ac.x.toString());
        formData.append(`modelConfigurations[${i}][atomCoordinates][${j}][y]`, ac.y.toString());
        formData.append(`modelConfigurations[${i}][atomCoordinates][${j}][z]`, ac.z.toString());
      });
    });
    formData.append('options[larmorFrequency]', command.options.larmorFrequency.toString());
    formData.append('options[correlationTime]', command.options.correlationTime.toString());
    formData.append('options[cutoffDistance]', command.options.cutoffDistance.toString());
    formData.append('options[ligandConcentration]', command.options.ligandConcentration.toString());
    formData.append('options[receptorConcentration]', command.options.receptorConcentration.toString());
    formData.append('options[dissociationConstant]', command.options.dissociationConstant.toString());

    formData.append('amberTopology', command.amberTopology as File);
    formData.append('amberTrajectory', command.amberTrajectory as File);

    return Axios.post<CalculateResponse>(
      `${endpoint}/calculate`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )
      .then(({ data }) => {
        commit(SUCCESS_STATUS);
        commit(SET_RESULT, data);

        return resolve();
      })
      .catch(() => {
        commit(ERROR_STATUS, 'Error performing calculation');

        return reject();
      });
  }),
  [SET_RESULT]: (
    { commit },
    calculationFile: File,
  ) => new Promise<void>((resolve, reject) => {
    const reader = new FileReader();
    commit(LOADING_STATUS);

    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result as string);

        commit(SUCCESS_STATUS);
        commit(SET_RESULT, data);

        return resolve();
      } catch (error) {
        commit(ERROR_STATUS, 'Error loading calculation file');

        return reject();
      }
    };

    reader.readAsText(calculationFile);
  }),
  [CLEAR_STATE]: ({ commit }) => new Promise<void>((resolve) => {
    commit(CLEAR_STATE);

    return resolve();
  }),
};

const getters: GetterTree<RedMatCalculationState, object> = {
  [IS_NEW]: (state) => state.status === NEW_STATUS,
  [IS_LOADING]: (state) => state.status === LOADING_STATUS,
  [IS_ERROR]: (state) => state.status === ERROR_STATUS,
  [MESSAGE]: (state) => state.statusMessage,
  [HAS_RESULT]: (state) => state.result !== null,
};

const RedMatCalculationModule: Module<RedMatCalculationState, object> = {
  state: redMatCalculationState,
  mutations,
  actions,
  getters,
};

export default RedMatCalculationModule;
